import { useState, useRef, useEffect } from "react";
import { Check, ChevronDown, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

export interface MultiSelectOption {
  value: string;
  label: string;
  color?: string;
  badge?: boolean;
}

interface MultiSelectProps {
  options: MultiSelectOption[];
  value: string[];
  onChange: (values: string[]) => void;
  placeholder?: string;
  label?: string;
  maxDisplay?: number;
  disabled?: boolean;
  className?: string;
}

export default function MultiSelect({
  options,
  value,
  onChange,
  placeholder = "Select options...",
  label,
  maxDisplay = 3,
  disabled = false,
  className = "",
}: MultiSelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        setSearchQuery("");
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const filteredOptions = options.filter((option) =>
    option.label.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  const handleToggle = (optionValue: string) => {
    if (disabled) return;

    const newValue = value.includes(optionValue)
      ? value.filter((v) => v !== optionValue)
      : [...value, optionValue];

    onChange(newValue);
  };

  const handleSelectAll = () => {
    if (disabled) return;
    onChange(filteredOptions.map((opt) => opt.value));
  };

  const handleClearAll = () => {
    if (disabled) return;
    onChange([]);
  };

  const handleRemove = (optionValue: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (disabled) return;
    onChange(value.filter((v) => v !== optionValue));
  };

  const getSelectedLabels = () => {
    return value
      .map((v) => options.find((opt) => opt.value === v)?.label)
      .filter(Boolean);
  };

  const selectedLabels = getSelectedLabels();
  const displayLabels =
    selectedLabels.length > maxDisplay
      ? selectedLabels.slice(0, maxDisplay)
      : selectedLabels;
  const remainingCount = Math.max(0, selectedLabels.length - maxDisplay);

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      {label && (
        <label className="block text-sm font-medium text-foreground mb-2">
          {label}
        </label>
      )}

      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={`
          w-full flex items-center justify-between gap-2
          px-4 py-2.5 bg-muted/50 border border-border/50 rounded-lg cursor-pointer
          text-sm text-left transition-all
          ${isOpen ? "ring-2 ring-primary/50 border-primary" : ""}
          ${disabled ? "opacity-50 cursor-not-allowed" : "hover:bg-muted cursor-pointer"}
        `}
      >
        <div className="flex-1 flex items-center gap-2 flex-wrap min-h-[20px]">
          {value.length === 0 ? (
            <span className="text-muted-foreground">{placeholder}</span>
          ) : (
            <>
              {displayLabels.map((label, idx) => {
                const option = options.find((opt) => opt.label === label);
                return (
                  <span
                    key={idx}
                    className={`
                      inline-flex items-center gap-1 px-2 py-0.5
                      rounded text-xs font-medium
                      ${option?.color || "bg-primary/10 text-primary"}
                    `}
                  >
                    {label}
                    <button
                      onClick={(e) => handleRemove(option?.value || "", e)}
                      disabled={disabled}
                      className="hover:bg-black/10 rounded-full p-0.5 transition-colors cursor-pointer"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                );
              })}
              {remainingCount > 0 && (
                <span className="px-2 py-0.5 bg-muted text-muted-foreground rounded text-xs font-medium">
                  +{remainingCount} more
                </span>
              )}
            </>
          )}
        </div>

        <ChevronDown
          className={`w-4 h-4 text-muted-foreground transition-transform flex-shrink-0 ${
            isOpen ? "rotate-180" : ""
          }`}
        />
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.15 }}
            className="absolute z-50 w-full mt-2 bg-card border border-border/50 rounded-lg shadow-xl overflow-hidden"
          >
            <div className="p-2 border-b border-border/50">
              <input
                ref={inputRef}
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search..."
                className="w-full px-3 py-2 bg-muted/50 border border-border/50 rounded text-sm focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all"
                onClick={(e) => e.stopPropagation()}
              />
            </div>

            <div className="flex items-center justify-between p-2 border-b border-border/50 bg-muted/30">
              <button
                onClick={handleSelectAll}
                disabled={disabled || filteredOptions.length === 0}
                className="text-xs text-primary hover:text-primary/80 font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors cursor-pointer"
              >
                Select All ({filteredOptions.length})
              </button>
              <button
                onClick={handleClearAll}
                disabled={disabled || value.length === 0}
                className="text-xs text-destructive hover:text-destructive/80 font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors cursor-pointer"
              >
                Clear All
              </button>
            </div>

            <div className="max-h-64 overflow-y-auto">
              {filteredOptions.length === 0 ? (
                <div className="p-4 text-center text-sm text-muted-foreground">
                  No options found
                </div>
              ) : (
                filteredOptions.map((option) => {
                  const isSelected = value.includes(option.value);
                  return (
                    <button
                      key={option.value}
                      onClick={() => handleToggle(option.value)}
                      disabled={disabled}
                      className={`
                        w-full flex items-center gap-3 px-4 py-2.5 cursor-pointer
                        text-sm text-left transition-colors
                        ${
                          isSelected
                            ? "bg-primary/10 text-primary"
                            : "hover:bg-muted/50 text-foreground"
                        }
                        ${disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}
                      `}
                    >
                      <div
                        className={`
                        w-4 h-4 flex items-center justify-center
                        border-2 rounded
                        ${
                          isSelected
                            ? "border-primary bg-primary"
                            : "border-border"
                        }
                        transition-all
                      `}
                      >
                        {isSelected && <Check className="w-3 h-3 text-white" />}
                      </div>

                      <span className="flex-1">{option.label}</span>

                      {option.badge && (
                        <span
                          className={`px-2 py-0.5 rounded text-xs font-medium ${
                            option.color ||
                            "bg-primary/10 text-primary border border-primary/20"
                          }`}
                        >
                          {option.value}
                        </span>
                      )}
                    </button>
                  );
                })
              )}
            </div>

            {value.length > 0 && (
              <div className="p-2 border-t border-border/50 bg-muted/30 text-xs text-muted-foreground text-center">
                {value.length} selected
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
